import { useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  SUBSCRIPTION_STATUS,
  type SubscriptionStatus,
} from "@/components/subscription-labels";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Gift, Plus, ShieldCheck } from "lucide-react";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";
import { Pagination } from "@/components/pagination";
import { CouponDialog } from "@/components/coupon-dialog";

export const Route = createFileRoute("/_authenticated/clients")({
  component: ClientsPage,
});

type ClientRow = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  loyaltyPoints: number;
};

const QUICK_AMOUNTS = [10, 50, 100];

function timeAgo(date: Date | string | null) {
  if (!date) return "Nigdy";
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "Teraz";
  if (diffMin < 60) return `${diffMin} min temu`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h temu`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d temu`;
  return d.toLocaleDateString("pl-PL");
}

function activityBadge(date: Date | string | null) {
  if (!date) return { label: "Nigdy", variant: "outline" as const };
  const diffMs = new Date().getTime() - new Date(date).getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < 7) return { label: "Aktywny", variant: "default" as const };
  if (diffDays < 30) return { label: "Ostatnio", variant: "secondary" as const };
  return { label: "Nieaktywny", variant: "outline" as const };
}

function clientName(c: Pick<ClientRow, "firstName" | "lastName">) {
  return [c.firstName, c.lastName].filter(Boolean).join(" ") || "—";
}

function ClientsPage() {
  const [page, setPage] = useState(1);
  const [pointsFor, setPointsFor] = useState<ClientRow | null>(null);
  const [couponFor, setCouponFor] = useState<ClientRow | null>(null);
  const { data, isLoading } = trpc.clients.list.useQuery({ page, limit: 20 });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Klienci</h1>
        <p className="text-sm text-muted-foreground">
          {data?.total ?? 0} klientów
        </p>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Klient</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Dołączył</th>
                <th className="px-4 py-3 font-medium">Ostatnia aktywność</th>
                <th className="px-4 py-3 font-medium">Punkty</th>
                <th className="px-4 py-3 font-medium">Abonament</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((c) => {
                const activity = activityBadge(c.lastActiveAt);
                const name = clientName(c);
                return (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">
                      {name}
                      {(c.role === "owner" || c.role === "operator") && (
                        <Badge
                          variant="outline"
                          className="ml-2 text-xs"
                          title={
                            c.role === "owner"
                              ? "Właściciel salonu — ma dostęp do panelu"
                              : "Operator — ma dostęp do panelu"
                          }
                        >
                          <ShieldCheck />
                          Dostęp do panelu
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {c.email}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(c.joinedAt).toLocaleDateString("pl-PL")}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Badge variant={activity.variant}>
                          {activity.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {timeAgo(c.lastActiveAt)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold">
                      {c.loyaltyPoints}
                    </td>
                    <td className="px-4 py-3">
                      {c.subscription ? (
                        <Link
                          to="/subscriptions"
                          className="flex flex-wrap items-center gap-1.5 hover:underline"
                        >
                          <span>{c.subscription.planName}</span>
                          <Badge
                            variant={
                              SUBSCRIPTION_STATUS[
                                c.subscription.status as SubscriptionStatus
                              ]?.variant ?? "outline"
                            }
                            className="text-xs"
                          >
                            {SUBSCRIPTION_STATUS[
                              c.subscription.status as SubscriptionStatus
                            ]?.label ?? c.subscription.status}
                          </Badge>
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPointsFor(c)}
                      >
                        <Plus className="mr-1 size-4" />
                        Punkty
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCouponFor(c)}
                      >
                        <Gift className="mr-1 size-4" />
                        Kupon
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {data?.items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Brak klientów
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-3">
          <Pagination
            page={page}
            totalPages={data?.totalPages ?? 1}
            onPageChange={setPage}
          />
        </div>
      </Card>

      <AdjustPointsDialog
        client={pointsFor}
        onClose={() => setPointsFor(null)}
      />

      <CouponDialog
        open={!!couponFor}
        onOpenChange={(open) => {
          if (!open) setCouponFor(null);
        }}
        presetRecipient={couponFor}
      />
    </div>
  );
}

function AdjustPointsDialog({
  client,
  onClose,
}: {
  client: ClientRow | null;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");

  // Every close path funnels through here so the next client the dialog
  // opens for never inherits the previous one's amount or reason.
  function close() {
    setPoints("");
    setReason("");
    onClose();
  }

  const adjustPoints = trpc.clients.adjustPoints.useMutation({
    onSuccess: (result) => {
      utils.clients.list.invalidate();
      toast.success(
        result.points > 0
          ? `Dodano ${result.points} pkt — saldo: ${result.balance}`
          : `Odjęto ${-result.points} pkt — saldo: ${result.balance}`,
      );
      close();
    },
    onError: (err) => toast.error(err.message),
  });

  const parsed = Number(points);
  const isValidNumber =
    points.trim() !== "" && Number.isInteger(parsed) && Math.abs(parsed) <= 100000;
  const newBalance = (client?.loyaltyPoints ?? 0) + (isValidNumber ? parsed : 0);
  const canSubmit =
    !!client && isValidNumber && parsed !== 0 && newBalance >= 0;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || !client) return;
    adjustPoints.mutate({
      userId: client.id,
      points: parsed,
      reason: reason.trim() || null,
    });
  }

  return (
    <Dialog
      open={!!client}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Przyznaj punkty</DialogTitle>
        </DialogHeader>
        {client && (
          <DialogForm onSubmit={onSubmit}>
            <DialogBody className="space-y-4">
              <div className="rounded-md bg-muted px-3 py-2 text-sm">
                <div className="font-medium">{clientName(client)}</div>
                <div className="text-muted-foreground">
                  {client.email} · saldo: {client.loyaltyPoints} pkt
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="points">Liczba punktów</Label>
                <div className="flex gap-2">
                  {QUICK_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPoints(String(amount))}
                    >
                      +{amount}
                    </Button>
                  ))}
                </div>
                <Input
                  id="points"
                  type="number"
                  step={1}
                  placeholder="np. 50"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Wpisz wartość ujemną (np. -20), aby odjąć punkty.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Powód (opcjonalnie)</Label>
                <Input
                  id="reason"
                  maxLength={200}
                  placeholder="np. Bonus za polecenie"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Powód będzie widoczny dla klienta w historii punktów.
                </p>
              </div>

              {isValidNumber && parsed !== 0 && (
                <p
                  className={
                    newBalance < 0
                      ? "text-sm text-destructive"
                      : "text-sm text-muted-foreground"
                  }
                >
                  {newBalance < 0
                    ? `Klient ma tylko ${client.loyaltyPoints} pkt — nie można zejść poniżej zera.`
                    : `Nowe saldo: ${newBalance} pkt`}
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                Anuluj
              </Button>
              <Button type="submit" disabled={!canSubmit || adjustPoints.isPending}>
                {adjustPoints.isPending ? "Zapisywanie..." : "Zapisz"}
              </Button>
            </DialogFooter>
          </DialogForm>
        )}
      </DialogContent>
    </Dialog>
  );
}
